import { useEffect, useState } from 'react';
import type { Job } from '@cef-ai/vault-sdk';

interface JobsHandle {
  jobs: { list(o?: { limit?: number }): Promise<{ items: Job[] }> };
}

/**
 * Single vault-wide jobs poll — the only new data source for the one-scroll
 * home. Feeds both the activity feed and the block order. ~10s cadence,
 * matching the old per-agent polls it replaces.
 */
export function useVaultJobs(
  vaultHandle: JobsHandle | null,
  opts: { limit?: number; intervalMs?: number } = {},
): { jobs: Job[]; loaded: boolean } {
  const { limit = 40, intervalMs = 10_000 } = opts;
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!vaultHandle) return;
    let cancelled = false;
    const load = async () => {
      try {
        const page = await vaultHandle.jobs.list({ limit });
        // Defensive: an unexpected/malformed response must never make `jobs`
        // non-iterable — the whole one-scroll home reduces over it, so a bad
        // shape here would crash the vault, not just the feed.
        if (!cancelled) setJobs(Array.isArray(page?.items) ? page.items : []);
      } catch {
        /* transient — keep last good jobs, try again next tick */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    void load();
    const timer = setInterval(() => { void load(); }, intervalMs);
    return () => { cancelled = true; clearInterval(timer); };
  }, [vaultHandle, limit, intervalMs]);

  return { jobs, loaded };
}
